import { cn } from '@sohanemon/utils';
import { m } from '@sohanscript/i18n';
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@sohanscript/ui/select';
import type { ComponentProps } from 'react';
import { useEffect, useRef, useState } from 'react';
import { getService } from '@/lib/data/services';
import { localizeServices } from '@/lib/data/services-i18n';
import { api } from '@/lib/trpc/client';
import { Button } from './ui/button';

// NOTE: ContactForm writes `?ready` via history.replaceState (bypasses the
// router), so listeners use this event to stay in sync.
export const READY_CHANGE_EVENT = 'sohanscript:ready-change';

type ContactFormProps = {
	// NOTE: Only the /start page listens for this (astronaut morph).
	syncReady?: boolean;
};

function Field({
	className,
	...props
}: ComponentProps<'input'> & { className?: string }) {
	return (
		<input
			className={cn(
				'w-full border-border border-b bg-transparent py-2.5 text-foreground text-sm outline-none',
				'transition-colors duration-200 placeholder:text-foreground/30',
				'focus:border-primary focus:placeholder:text-foreground/20',
				className,
			)}
			{...props}
		/>
	);
}

function readServiceParam(): string {
	if (typeof window === 'undefined') return '';
	const slug = new URLSearchParams(window.location.search).get('service') ?? '';
	return getService(slug) || slug === 'other' ? slug : '';
}

export function ContactForm({ syncReady = false }: ContactFormProps) {
	const formRef = useRef<HTMLFormElement>(null);
	const [subject, setSubject] = useState<string | null>(null);
	const [service, setService] = useState<string | null>(
		() => readServiceParam() || null,
	);

	// NOTE: Values stay English so the emailed subject reads well;
	// labels are localized per locale.
	const subjectOptions = [
		{ value: 'Project inquiry', label: m.form_subject_option_project() },
		{ value: 'Freelance work', label: m.form_subject_option_freelance() },
		{ value: 'Collaboration', label: m.form_subject_option_collaboration() },
		{ value: 'Other', label: m.form_subject_option_other() },
	];

	// NOTE: Service values are slugs so `?service=<slug>` preselects;
	// the emailed subject uses the resolved title.
	const serviceOptions = [
		...localizeServices().map((s) => ({ value: s.slug, label: s.title })),
		{ value: 'other', label: m.form_service_other() },
	];

	// NOTE: Reflect editing state in the URL without rerendering the route.
	useEffect(() => {
		if (!syncReady) return;
		return () => {
			if (typeof window === 'undefined') return;
			const url = new URL(window.location.href);
			if (url.searchParams.has('ready')) {
				url.searchParams.delete('ready');
				window.history.replaceState(null, '', url);
				window.dispatchEvent(new Event(READY_CHANGE_EVENT));
			}
		};
	}, [syncReady]);

	function syncReadyParam() {
		if (!syncReady) return;
		const form = formRef.current;
		if (!form || typeof window === 'undefined') return;
		// NOTE: Blur fires before the next field receives focus, so read after focus settles.
		requestAnimationFrame(() => {
			if (!form.isConnected) return;
			const isFocused = form.contains(document.activeElement);
			const fields = form.querySelectorAll<
				HTMLInputElement | HTMLTextAreaElement
			>('input[name], textarea[name]');
			const hasText = [...fields].some((field) => field.value.trim() !== '');
			const url = new URL(window.location.href);
			if (isFocused || hasText) {
				url.searchParams.set('ready', 'true');
			} else {
				url.searchParams.delete('ready');
			}
			window.history.replaceState(null, '', url);
			window.dispatchEvent(new Event(READY_CHANGE_EVENT));
		});
	}

	const {
		mutate: sendEmail,
		isPending,
		isSuccess,
	} = api.email.send.useMutation({
		meta: {
			alerts: {
				success: m.form_alert_success(),
				error: m.form_alert_error(),
				loading: m.form_alert_loading(),
			},
		},
	});

	function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();
		const form = new FormData(e.currentTarget);
		const picked = getService(service ?? '');
		sendEmail({
			name: form.get('name') as string,
			email: form.get('email') as string,
			subject:
				(form.get('subject') as string) ||
				(picked ? `Service: ${picked.title}` : undefined),
			message: form.get('message') as string,
		});
	}

	return (
		<form
			ref={formRef}
			onSubmit={handleSubmit}
			onFocus={syncReadyParam}
			onBlur={syncReadyParam}
			onInput={syncReadyParam}
			className="mx-auto mt-8 flex w-full max-w-lg flex-col gap-6"
		>
			<div className="flex gap-6">
				<Field
					required
					minLength={3}
					name="name"
					placeholder={m.form_name_placeholder()}
					title={m.form_name_title()}
					type="text"
					aria-label={m.form_name_aria()}
				/>
				<Field
					required
					name="email"
					placeholder={m.form_email_placeholder()}
					title={m.form_email_title()}
					type="email"
					aria-label={m.form_email_aria()}
				/>
			</div>

			<Select
				name="subject"
				items={subjectOptions}
				value={subject}
				onValueChange={setSubject}
			>
				<SelectTrigger aria-label={m.form_subject_aria()}>
					<SelectValue placeholder={m.form_subject_placeholder()} />
				</SelectTrigger>
				<SelectContent>
					<SelectGroup>
						{subjectOptions.map((option) => (
							<SelectItem key={option.value} value={option.value}>
								{option.label}
							</SelectItem>
						))}
					</SelectGroup>
				</SelectContent>
			</Select>

			<Select
				name="service"
				items={serviceOptions}
				value={service}
				onValueChange={setService}
			>
				<SelectTrigger aria-label={m.form_service_aria()}>
					<SelectValue placeholder={m.form_service_label()} />
				</SelectTrigger>
				<SelectContent>
					<SelectGroup>
						{serviceOptions.map((option) => (
							<SelectItem key={option.value} value={option.value}>
								{option.label}
							</SelectItem>
						))}
					</SelectGroup>
				</SelectContent>
			</Select>

			<textarea
				required
				minLength={5}
				name="message"
				placeholder={m.form_message_placeholder()}
				rows={4}
				aria-label={m.form_message_aria()}
				className={cn(
					'w-full resize-none border-border border-b bg-transparent py-2.5 text-foreground text-sm outline-none',
					'transition-colors duration-200 placeholder:text-foreground/30',
					'focus:border-primary focus:placeholder:text-foreground/20',
				)}
			/>

			<Button
				type="submit"
				variant="outline"
				size="lg"
				icon="line-md:telegram"
				aria-label={
					isPending
						? m.form_send_pending_aria()
						: isSuccess
							? m.form_send_success_aria()
							: m.form_send_idle_aria()
				}
				disabled={isPending || isSuccess}
				className={cn(
					'disabled:cursor-not-allowed disabled:opacity-50',
					isSuccess && 'bg-primary/60',
				)}
			>
				{isPending
					? m.form_send_pending()
					: isSuccess
						? m.form_send_success()
						: m.form_send_idle()}
			</Button>
		</form>
	);
}
